import nacl from 'tweetnacl';
import { encodeBase64, decodeBase64 } from 'tweetnacl-util';

// Use native TextEncoder/TextDecoder for UTF-8 — handles large media payloads
// efficiently unlike tweetnacl-util's encodeUTF8/decodeUTF8 which use
// character-by-character string concatenation and can fail on multi-MB payloads.
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

export interface KeyPair {
  publicKey: string;
  secretKey: string;
}

export interface EncryptedMessage {
  ciphertext: string;
  nonce: string;
  ephemeralPublicKey: string;
}

// Generate a new Ed25519 signing keypair
export function generateSigningKeyPair(): KeyPair {
  const keyPair = nacl.sign.keyPair();
  return {
    publicKey: encodeBase64(keyPair.publicKey),
    secretKey: encodeBase64(keyPair.secretKey),
  };
}

// Generate a new X25519 encryption keypair
export function generateEncryptionKeyPair(): KeyPair {
  const keyPair = nacl.box.keyPair();
  return {
    publicKey: encodeBase64(keyPair.publicKey),
    secretKey: encodeBase64(keyPair.secretKey),
  };
}

// Generate UID from public key hash (first 16 chars of base64 hash)
export function generateUID(publicKey: string): string {
  const publicKeyBytes = decodeBase64(publicKey);
  const hash = nacl.hash(publicKeyBytes);
  const hashBase64 = encodeBase64(hash);
  // Create a more readable UID format: XXXX-XXXX-XXXX-XXXX
  const cleanHash = hashBase64.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  return `${cleanHash.slice(0, 4)}-${cleanHash.slice(4, 8)}-${cleanHash.slice(8, 12)}-${cleanHash.slice(12, 16)}`;
}

// Encrypt a message for a recipient
export function encryptMessage(
  message: string,
  recipientPublicKey: string,
  senderSecretKey: string
): EncryptedMessage {
  const messageBytes = new Uint8Array(textEncoder.encode(message));
  const nonce = nacl.randomBytes(nacl.box.nonceLength);
  const recipientPubKeyBytes = decodeBase64(recipientPublicKey);
  const senderSecKeyBytes = decodeBase64(senderSecretKey);

  const encrypted = nacl.box(
    messageBytes,
    nonce,
    recipientPubKeyBytes,
    senderSecKeyBytes
  );

  return {
    ciphertext: encodeBase64(encrypted),
    nonce: encodeBase64(nonce),
    ephemeralPublicKey: '', // For future forward secrecy implementation
  };
}

// Decrypt a message from a sender
export function decryptMessage(
  encryptedMessage: EncryptedMessage,
  senderPublicKey: string,
  recipientSecretKey: string
): string | null {
  try {
    const ciphertext = decodeBase64(encryptedMessage.ciphertext);
    const nonce = decodeBase64(encryptedMessage.nonce);
    const senderPubKeyBytes = decodeBase64(senderPublicKey);
    const recipientSecKeyBytes = decodeBase64(recipientSecretKey);

    const decrypted = nacl.box.open(
      ciphertext,
      nonce,
      senderPubKeyBytes,
      recipientSecKeyBytes
    );

    if (!decrypted) return null;
    return textDecoder.decode(decrypted);
  } catch (e) {
    console.error('[Crypto] Decryption error:', e);
    return null;
  }
}

// Sign a message
export function signMessage(message: string, secretKey: string): string {
  const messageBytes = new Uint8Array(textEncoder.encode(message));
  const secretKeyBytes = decodeBase64(secretKey);
  const signature = nacl.sign.detached(messageBytes, secretKeyBytes);
  return encodeBase64(signature);
}

// Verify a signed message
export function verifySignature(
  message: string,
  signature: string,
  publicKey: string
): boolean {
  try {
    const messageBytes = new Uint8Array(textEncoder.encode(message));
    const signatureBytes = decodeBase64(signature);
    const publicKeyBytes = decodeBase64(publicKey);
    return nacl.sign.detached.verify(messageBytes, signatureBytes, publicKeyBytes);
  } catch {
    return false;
  }
}

// Key derivation iterations (high for security)
const PBKDF2_ITERATIONS = 100000;

// Derive a key from passphrase using PBKDF2 (async, browser-native)
async function deriveKeyFromPassphrase(
  passphrase: string,
  salt: Uint8Array
): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const passphraseBytes = encoder.encode(passphrase);
  
  // Import passphrase as key material
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    passphraseBytes,
    'PBKDF2',
    false,
    ['deriveBits']
  );
  
  // Derive key using PBKDF2-SHA256
  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: salt.buffer as ArrayBuffer,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    nacl.secretbox.keyLength * 8
  );
  
  return new Uint8Array(derivedBits);
}

// Legacy synchronous key derivation (only for decrypting old version 1/2 data)
function deriveKeyFromPassphraseLegacy(
  passphrase: string,
  salt: Uint8Array
): Uint8Array {
  const passphraseBytes = textEncoder.encode(passphrase);
  let hash = nacl.hash(new Uint8Array([...passphraseBytes, ...salt]));
  for (let i = 0; i < 1000; i++) {
    hash = nacl.hash(hash);
  }
  return hash.slice(0, nacl.secretbox.keyLength);
}

// Encrypt keys for local storage using a passphrase with proper PBKDF2 key derivation
export async function encryptKeysForStorage(
  keys: { signing: KeyPair; encryption: KeyPair },
  passphrase: string
): Promise<string> {
  const data = JSON.stringify(keys);
  const dataBytes = textEncoder.encode(data);
  
  // Generate random salt for key derivation
  const salt = nacl.randomBytes(32);
  
  // Derive key using async PBKDF2 with 100,000 iterations
  const key = await deriveKeyFromPassphrase(passphrase, salt);
  
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
  const encrypted = nacl.secretbox(dataBytes, nonce, key);
  
  return JSON.stringify({
    encrypted: encodeBase64(encrypted),
    nonce: encodeBase64(nonce),
    salt: encodeBase64(salt),
    version: 3, // Version 3: async PBKDF2 with 100k iterations
  });
}

// Decrypt keys from local storage
export async function decryptKeysFromStorage(
  encryptedData: string,
  passphrase: string
): Promise<{ signing: KeyPair; encryption: KeyPair } | null> {
  try {
    const parsed = JSON.parse(encryptedData);
    const { encrypted, nonce, salt, version } = parsed;
    const encryptedBytes = decodeBase64(encrypted);
    const nonceBytes = decodeBase64(nonce);
    
    let key: Uint8Array;
    
    if (version === 3 && salt) {
      // Version 3: async PBKDF2 with 100k iterations
      const saltBytes = decodeBase64(salt);
      key = await deriveKeyFromPassphrase(passphrase, saltBytes);
    } else if (version === 2 && salt) {
      // Legacy version 2: sync hash with 1000 rounds
      const saltBytes = decodeBase64(salt);
      key = deriveKeyFromPassphraseLegacy(passphrase, saltBytes);
    } else {
      // Legacy version 1: simple hash
      const passphraseBytes = textEncoder.encode(passphrase);
      const keyHash = nacl.hash(passphraseBytes);
      key = keyHash.slice(0, nacl.secretbox.keyLength);
    }
    
    const decrypted = nacl.secretbox.open(encryptedBytes, nonceBytes, key);
    if (!decrypted) return null;
    
    return JSON.parse(textDecoder.decode(decrypted));
  } catch {
    return null;
  }
}

// Generate a random recovery phrase (12 words) using cryptographically secure randomness
export function generateRecoveryPhrase(): string[] {
  const words = [
    'abandon', 'ability', 'able', 'about', 'above', 'absent', 'absorb', 'abstract',
    'absurd', 'abuse', 'access', 'accident', 'account', 'accuse', 'achieve', 'acid',
    'acoustic', 'acquire', 'across', 'act', 'action', 'actor', 'actress', 'actual',
    'adapt', 'add', 'addict', 'address', 'adjust', 'admit', 'adult', 'advance',
    'advice', 'aerobic', 'affair', 'afford', 'afraid', 'again', 'age', 'agent',
    'agree', 'ahead', 'aim', 'air', 'airport', 'aisle', 'alarm', 'album',
    'alert', 'alien', 'all', 'alley', 'allow', 'almost', 'alone', 'alpha',
    'already', 'also', 'alter', 'always', 'amateur', 'amazing', 'among', 'amount',
  ];
  
  const phrase: string[] = [];
  // Use cryptographically secure random for security-sensitive operations
  const randomValues = new Uint32Array(12);
  crypto.getRandomValues(randomValues);
  
  for (let i = 0; i < 12; i++) {
    const randomIndex = randomValues[i] % words.length;
    phrase.push(words[randomIndex]);
  }
  return phrase;
}
